import {
  DurationValueSchema,
  MoneyValueSchema,
  QuestionnaireBusinessTypeValueSchema,
  QuestionnaireDefinitionSchema,
  QuestionnaireFileValueSchema,
  QuestionnaireIndustryCategoriesValueSchema,
  SensitiveSavedValueSchema,
  type QuestionnaireAnswers,
  type QuestionnaireCondition,
  type QuestionnaireDefinition,
  type QuestionnaireField,
  type QuestionnaireStep,
} from "./schema";

export type QuestionnaireFieldError = {
  fieldId: string;
  binding: string;
  message: string;
};

export type QuestionnaireStepValidation = {
  success: boolean;
  values: QuestionnaireAnswers;
  errors: QuestionnaireFieldError[];
};

export type QuestionnaireProgress = {
  answered: number;
  total: number;
  percentage: number;
  current: number;
};

export type QuestionnaireFlowState = {
  definitionId: string;
  definitionVersion: string;
  currentStep: QuestionnaireStep;
  visibleSteps: QuestionnaireStep[];
  nextStep: QuestionnaireStep | null;
  previousStep: QuestionnaireStep | null;
  progress: QuestionnaireProgress;
};

export function getAnswer(
  answers: QuestionnaireAnswers,
  binding: string,
): unknown {
  if (Object.prototype.hasOwnProperty.call(answers, binding)) {
    return answers[binding];
  }

  let value: unknown = answers;
  for (const part of binding.split(".")) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return undefined;
    }
    value = (value as Record<string, unknown>)[part];
  }
  return value;
}

export function setAnswer(
  answers: QuestionnaireAnswers,
  binding: string,
  value: unknown,
): QuestionnaireAnswers {
  return { ...answers, [binding]: value };
}

export function evaluateCondition(
  condition: QuestionnaireCondition | undefined,
  answers: QuestionnaireAnswers,
): boolean {
  if (!condition) return true;

  switch (condition.op) {
    case "equals":
      return getAnswer(answers, condition.field) === condition.value;
    case "hasValue":
      return hasValue(getAnswer(answers, condition.field));
    case "greaterThan": {
      const value = getAnswer(answers, condition.field);
      const numeric =
        typeof value === "number"
          ? value
          : moneyAmountMinor(value);
      return numeric != null && numeric > condition.value;
    }
    case "in":
      return condition.values.some(
        (candidate) => getAnswer(answers, condition.field) === candidate,
      );
    case "and":
      return condition.conditions.every((item) =>
        evaluateCondition(item, answers),
      );
    case "or":
      return condition.conditions.some((item) =>
        evaluateCondition(item, answers),
      );
    case "not":
      return !evaluateCondition(condition.condition, answers);
  }
}

export function isFieldVisible(
  field: QuestionnaireField,
  answers: QuestionnaireAnswers,
): boolean {
  return evaluateCondition(field.visibleWhen, answers);
}

export function isFieldRequired(
  field: QuestionnaireField,
  answers: QuestionnaireAnswers,
): boolean {
  return (
    field.required ||
    Boolean(
      field.requiredWhen &&
        evaluateCondition(field.requiredWhen, answers),
    )
  );
}

export function isAnswered(
  field: QuestionnaireField,
  answers: QuestionnaireAnswers,
): boolean {
  if (!isFieldVisible(field, answers)) return true;
  if (field.kind === "computed_display" || field.kind === "page") return true;

  const value = getAnswer(answers, field.binding);
  if (
    (field.sensitivity === "sensitive" ||
      field.sensitivity === "participant") &&
    SensitiveSavedValueSchema.safeParse(value).success
  ) {
    return true;
  }
  if (field.kind === "consent") return value === true;
  if (field.kind === "yes_no") return typeof value === "boolean";
  if (field.kind === "money") return MoneyValueSchema.safeParse(value).success;
  if (field.kind === "business_type") {
    return QuestionnaireBusinessTypeValueSchema.safeParse(value).success;
  }
  if (field.kind === "industry_categories") {
    if (typeof value === "string" && value.trim().length > 0) return true;
    return (
      QuestionnaireIndustryCategoriesValueSchema.safeParse(value).success &&
      Array.isArray(value) &&
      value.length > 0
    );
  }
  if (field.kind === "file") {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    return (
      values.length > 0 &&
      values.every(
        (item) => QuestionnaireFileValueSchema.safeParse(item).success,
      )
    );
  }
  if (field.kind === "repeatable_group") {
    return Array.isArray(value) && value.length > 0;
  }
  if (field.kind === "sensitive_identifier") {
    return (
      SensitiveSavedValueSchema.safeParse(value).success ||
      (typeof value === "string" && value.trim().length > 0)
    );
  }
  if (field.kind === "multi_select") {
    return Array.isArray(value) && value.length > 0;
  }
  if (field.kind === "duration") {
    return DurationValueSchema.safeParse(value).success;
  }
  if (field.kind === "participant_invite") {
    return Array.isArray(value) && value.length > 0;
  }
  return hasValue(value);
}

export function visibleFields(
  step: QuestionnaireStep,
  answers: QuestionnaireAnswers,
): QuestionnaireField[] {
  return step.fields.filter((field) => isFieldVisible(field, answers));
}

export function visibleSteps(
  definition: QuestionnaireDefinition,
  answers: QuestionnaireAnswers,
): QuestionnaireStep[] {
  return definition.steps.filter((step) =>
    evaluateCondition(step.visibleWhen, answers),
  );
}

export function getNextStep(
  definition: QuestionnaireDefinition,
  stepId: string,
  answers: QuestionnaireAnswers,
): QuestionnaireStep | null {
  const current = definition.steps.find((step) => step.id === stepId);
  if (!current) return null;

  if (current.transitions?.length) {
    const transition = current.transitions.find((item) =>
      evaluateCondition(item.when, answers),
    );
    if (transition) {
      const target = definition.steps.find(
        (step) => step.id === transition.to,
      );
      return target && evaluateCondition(target.visibleWhen, answers)
        ? target
        : null;
    }
  }

  const steps = visibleSteps(definition, answers);
  const index = steps.findIndex((step) => step.id === stepId);
  return index >= 0 ? (steps[index + 1] ?? null) : null;
}

export function getPreviousStep(
  definition: QuestionnaireDefinition,
  stepId: string,
  answers: QuestionnaireAnswers,
): QuestionnaireStep | null {
  const steps = visibleSteps(definition, answers);
  const index = steps.findIndex((step) => step.id === stepId);
  return index > 0 ? (steps[index - 1] ?? null) : null;
}

export function questionnaireProgress(
  definition: QuestionnaireDefinition,
  answers: QuestionnaireAnswers,
  currentStepId?: string,
): QuestionnaireProgress {
  const fields = visibleSteps(definition, answers)
    .flatMap((step) => visibleFields(step, answers))
    .filter((field) => field.kind !== "computed_display");
  const answered = fields.filter((field) => isAnswered(field, answers)).length;
  const total = fields.length;
  const currentStepIndex = currentStepId
    ? visibleSteps(definition, answers).findIndex(
        (step) => step.id === currentStepId,
      )
    : -1;

  return {
    answered,
    total,
    percentage: total === 0 ? 100 : Math.round((answered / total) * 100),
    current: currentStepIndex >= 0 ? currentStepIndex + 1 : 1,
  };
}

export function evaluateFlow(input: {
  definition: QuestionnaireDefinition;
  answers: QuestionnaireAnswers;
  currentStepId?: string | null;
}): QuestionnaireFlowState {
  const definition = parseQuestionnaireDefinition(input.definition);
  const steps = visibleSteps(definition, input.answers);
  const requested = input.currentStepId
    ? steps.find((step) => step.id === input.currentStepId)
    : null;
  const initial =
    steps.find((step) => step.id === definition.initialStepId) ?? steps[0];

  if (!requested && !initial) {
    throw new Error("Questionnaire has no visible steps.");
  }

  const currentStep = requested ?? initial!;
  return {
    definitionId: definition.id,
    definitionVersion: definition.version,
    currentStep,
    visibleSteps: steps,
    nextStep: getNextStep(
      definition,
      currentStep.id,
      input.answers,
    ),
    previousStep: getPreviousStep(
      definition,
      currentStep.id,
      input.answers,
    ),
    progress: questionnaireProgress(
      definition,
      input.answers,
      currentStep.id,
    ),
  };
}

export function validateStep(input: {
  step: QuestionnaireStep;
  answers: QuestionnaireAnswers;
}): QuestionnaireStepValidation {
  const values: QuestionnaireAnswers = {};
  const errors: QuestionnaireFieldError[] = [];

  for (const field of visibleFields(input.step, input.answers)) {
    if (field.kind === "computed_display" || field.kind === "page") continue;

    const value = getAnswer(input.answers, field.binding);
    const required = isFieldRequired(field, input.answers);
    if (!hasValue(value)) {
      if (required) errors.push(fieldError(field, "This answer is required."));
      continue;
    }

    const message = validateFieldValue(field, value, input.answers);
    if (message) {
      errors.push(fieldError(field, message));
    } else {
      values[field.binding] = value;
    }
  }

  return { success: errors.length === 0, values, errors };
}

export function parseQuestionnaireDefinition(
  input: unknown,
): QuestionnaireDefinition {
  const definition = QuestionnaireDefinitionSchema.parse(input);
  const issues = definitionIntegrityIssues(definition);
  if (issues.length > 0) {
    throw new Error(`Invalid questionnaire definition: ${issues.join(" ")}`);
  }
  return definition;
}

export function definitionIntegrityIssues(
  definition: QuestionnaireDefinition,
): string[] {
  const issues: string[] = [];
  const stepIds = new Set<string>();
  const fieldIds = new Set<string>();
  const bindings = new Set<string>();

  for (const step of definition.steps) {
    if (stepIds.has(step.id)) issues.push(`Duplicate step id “${step.id}”.`);
    stepIds.add(step.id);

    for (const field of step.fields) {
      collectFieldIntegrity(field, fieldIds, bindings, issues);
    }
  }

  if (!stepIds.has(definition.initialStepId)) {
    issues.push(`Initial step “${definition.initialStepId}” does not exist.`);
  }
  for (const step of definition.steps) {
    for (const transition of step.transitions ?? []) {
      if (!stepIds.has(transition.to)) {
        issues.push(
          `Step “${step.id}” targets missing step “${transition.to}”.`,
        );
      }
    }
    const fallbacks = (step.transitions ?? []).filter((item) => !item.when);
    if (fallbacks.length > 1) {
      issues.push(`Step “${step.id}” has multiple fallback transitions.`);
    }
  }

  issues.push(...transitionCycleIssues(definition));
  return issues;
}

function validateFieldValue(
  field: QuestionnaireField,
  value: unknown,
  answers?: QuestionnaireAnswers,
): string | null {
  if (
    (field.sensitivity === "sensitive" ||
      field.sensitivity === "participant") &&
    SensitiveSavedValueSchema.safeParse(value).success
  ) {
    return null;
  }
  switch (field.kind) {
    case "text":
    case "long_text":
    case "phone":
    case "date": {
      if (typeof value !== "string") return "Enter a valid value.";
      const trimmed = value.trim();
      if (field.minLength != null && trimmed.length < field.minLength) {
        return `Enter at least ${field.minLength} characters.`;
      }
      if (field.maxLength != null && trimmed.length > field.maxLength) {
        return `Enter no more than ${field.maxLength} characters.`;
      }
      if (field.pattern) {
        try {
          if (!new RegExp(field.pattern).test(trimmed)) {
            return "Enter a value in the requested format.";
          }
        } catch {
          return "This field has an invalid validation pattern.";
        }
      }
      if (
        field.kind === "date" &&
        !/^\d{4}-\d{2}-\d{2}$/.test(trimmed)
      ) {
        return "Enter a valid date.";
      }
      return null;
    }
    case "enum":
      return typeof value === "string" &&
        field.options.some((option) => option.id === value)
        ? null
        : "Choose a valid option.";
    case "multi_select": {
      if (!Array.isArray(value)) return "Choose one or more valid options.";
      const allowed = new Set(field.options.map((option) => option.id));
      if (
        value.some(
          (item) => typeof item !== "string" || !allowed.has(item),
        )
      ) {
        return "Choose only valid options.";
      }
      if (
        field.minSelections != null &&
        value.length < field.minSelections
      ) {
        return `Choose at least ${field.minSelections} options.`;
      }
      if (
        field.maxSelections != null &&
        value.length > field.maxSelections
      ) {
        return `Choose no more than ${field.maxSelections} options.`;
      }
      return null;
    }
    case "duration":
      return DurationValueSchema.safeParse(value).success
        ? null
        : "Enter how long, in years or months.";
    case "yes_no":
      return typeof value === "boolean" ? null : "Choose Yes or No.";
    case "money": {
      const parsed = MoneyValueSchema.safeParse(value);
      if (!parsed.success) return "Enter a valid monetary amount.";
      if (!field.currencies.includes(parsed.data.currency)) {
        return "Choose a supported currency.";
      }
      if (field.currencyBinding && answers) {
        const applicationCurrency = getAnswer(
          answers,
          field.currencyBinding,
        );
        if (
          typeof applicationCurrency === "string" &&
          parsed.data.currency !== applicationCurrency
        ) {
          return `Use ${applicationCurrency} for all financial amounts.`;
        }
      }
      if (
        field.minimumMinor != null &&
        parsed.data.amountMinor < field.minimumMinor
      ) {
        return "Enter an amount above the minimum.";
      }
      if (
        field.maximumMinor != null &&
        parsed.data.amountMinor > field.maximumMinor
      ) {
        return "Enter an amount below the maximum.";
      }
      return null;
    }
    case "business_type":
      return QuestionnaireBusinessTypeValueSchema.safeParse(value).success
        ? null
        : "Choose a valid business type.";
    case "industry_categories": {
      if (typeof value === "string" && value.trim().length > 0) return null;
      if (!QuestionnaireIndustryCategoriesValueSchema.safeParse(value).success) {
        return "Choose one or more industries.";
      }
      if (!Array.isArray(value) || value.length === 0) {
        return "Choose one or more industries.";
      }
      return null;
    }
    case "file": {
      const files = Array.isArray(value) ? value : [value];
      if (
        files.some(
          (item) => !QuestionnaireFileValueSchema.safeParse(item).success,
        )
      ) {
        return "Choose a valid uploaded file.";
      }
      if (field.maxFiles != null && files.length > field.maxFiles) {
        return `Choose no more than ${field.maxFiles} files.`;
      }
      for (const item of files) {
        const file = QuestionnaireFileValueSchema.parse(item);
        if (
          field.acceptedMimeTypes?.length &&
          file.mimeType &&
          !field.acceptedMimeTypes.includes(file.mimeType)
        ) {
          return "Choose a supported file type.";
        }
        if (
          field.maxBytes != null &&
          file.size != null &&
          file.size > field.maxBytes
        ) {
          return "Choose a smaller file.";
        }
      }
      return null;
    }
    case "repeatable_group": {
      if (!Array.isArray(value)) return "Add at least one item.";
      if (field.minItems != null && value.length < field.minItems) {
        return `Add at least ${field.minItems} items.`;
      }
      if (field.maxItems != null && value.length > field.maxItems) {
        return `Add no more than ${field.maxItems} items.`;
      }
      for (const item of value) {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
          return "Complete each item.";
        }
        const answers = item as QuestionnaireAnswers;
        for (const child of field.fields) {
          if (!isFieldVisible(child, answers)) continue;
          const childValue = getAnswer(answers, child.binding);
          if (
            isFieldRequired(child, answers) &&
            !hasValue(childValue)
          ) {
            return `Complete “${child.label}” for each item.`;
          }
          if (hasValue(childValue)) {
            const childError = validateFieldValue(
              child,
              childValue,
              answers,
            );
            if (childError) return `${child.label}: ${childError}`;
          }
        }
      }
      return null;
    }
    case "sensitive_identifier": {
      if (SensitiveSavedValueSchema.safeParse(value).success) return null;
      if (typeof value !== "string") return "Enter a valid identifier.";
      const digits = value.replace(/\D/g, "");
      if (field.identifierType === "ssn" && digits.length !== 9) {
        return "Enter a valid Social Security number.";
      }
      if (field.identifierType === "tax_id" && digits.length !== 9) {
        return "Enter a valid tax identifier.";
      }
      return null;
    }
    case "consent":
      return value === true ? null : "Consent is required to continue.";
    case "participant_invite": {
      if (!Array.isArray(value) || value.length === 0) {
        return "Add the required participant.";
      }
      const valid = value.every((item) => {
        if (!item || typeof item !== "object") return false;
        const participant = item as Record<string, unknown>;
        return (
          typeof participant.email === "string" &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(participant.email) &&
          typeof participant.role === "string" &&
          field.roles.includes(
            participant.role as (typeof field.roles)[number],
          )
        );
      });
      return valid ? null : "Enter a valid participant email and role.";
    }
    case "computed_display":
    case "page":
      return null;
  }
}

function collectFieldIntegrity(
  field: QuestionnaireField,
  fieldIds: Set<string>,
  bindings: Set<string>,
  issues: string[],
  prefix = "",
): void {
  const qualifiedId = `${prefix}${field.id}`;
  const qualifiedBinding = `${prefix}${field.binding}`;
  if (fieldIds.has(qualifiedId)) {
    issues.push(`Duplicate field id “${field.id}”.`);
  }
  fieldIds.add(qualifiedId);
  if (bindings.has(qualifiedBinding)) {
    issues.push(`Duplicate field binding “${field.binding}”.`);
  }
  bindings.add(qualifiedBinding);

  if (
    field.kind === "money" &&
    !field.currencies.includes(field.defaultCurrency)
  ) {
    issues.push(
      `Money field “${field.id}” default currency is not supported by the field.`,
    );
  }
  if (
    field.kind === "money" &&
    field.minimumMinor != null &&
    field.maximumMinor != null &&
    field.minimumMinor > field.maximumMinor
  ) {
    issues.push(`Money field “${field.id}” has an invalid amount range.`);
  }
  if (field.kind === "repeatable_group") {
    for (const child of field.fields) {
      collectFieldIntegrity(
        child,
        fieldIds,
        bindings,
        issues,
        `${qualifiedId}.`,
      );
    }
  }
}

function transitionCycleIssues(
  definition: QuestionnaireDefinition,
): string[] {
  const issues: string[] = [];
  const visiting = new Set<string>();
  const visited = new Set<string>();

  const visit = (stepId: string): void => {
    if (visiting.has(stepId)) {
      issues.push(`Transition graph contains a cycle at “${stepId}”.`);
      return;
    }
    if (visited.has(stepId)) return;
    visiting.add(stepId);
    const step = definition.steps.find((item) => item.id === stepId);
    for (const transition of step?.transitions ?? []) {
      visit(transition.to);
    }
    visiting.delete(stepId);
    visited.add(stepId);
  };

  visit(definition.initialStepId);
  return [...new Set(issues)];
}

function fieldError(
  field: QuestionnaireField,
  message: string,
): QuestionnaireFieldError {
  return { fieldId: field.id, binding: field.binding, message };
}

function hasValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function moneyAmountMinor(value: unknown): number | null {
  const parsed = MoneyValueSchema.safeParse(value);
  return parsed.success ? parsed.data.amountMinor : null;
}
